/**
 * Sound-effect recipes (pure data). Every SFX is short, soft and pleasant:
 * sine/triangle voices through low-pass filters plus filtered noise — never a
 * square-wave buzzer. Pitched effects are written in semitones above the
 * current theme's key (REGISTER.sfx), using only intervals that exist in all
 * five theme scales (root, 2nd, 5th, 6th and their octaves), so a chime is
 * always in tune with the music underneath it.
 *
 * DOM-free (validated by unit tests); synth.ts turns these into WebAudio nodes.
 */
import type { SfxName } from '../contracts.ts';
import { midiToHz } from './pitch.ts';
import type { SfxRateRule } from './rateLimit.ts';
import { noteLifetimeS, type VoiceDef } from './timbres.ts';

/** A pitch relative to the theme's SFX root (semitones), or an absolute frequency. */
export type PitchRef = { semi: number } | { hz: number };

export interface SfxNote {
  /** Start offset (s) from the trigger. */
  at: number;
  pitch: PitchRef;
  /** Optional pitch glide target, reached after `glideS`. */
  glideTo?: PitchRef;
  glideS?: number;
  /** Written length (s). */
  dur: number;
  /** 0–1. */
  velocity: number;
}

export interface SfxToneLayer {
  kind: 'tone';
  voice: VoiceDef;
  notes: readonly SfxNote[];
}

export type NoiseFilterType = 'lowpass' | 'bandpass' | 'highpass';

export interface SfxNoiseLayer {
  kind: 'noise';
  at: number;
  dur: number;
  /** Peak level (0–1). */
  gain: number;
  attack: number;
  filter: { type: NoiseFilterType; hz: number; toHz?: number; q: number };
}

export type SfxLayer = SfxToneLayer | SfxNoiseLayer;

export interface SfxSpec extends SfxRateRule {
  layers: readonly SfxLayer[];
  /** Whether the music ducks (−6 dB / 350 ms) under this effect. Tiny UI ticks don't. */
  ducksMusic: boolean;
}

// ───────────── Voices ─────────────

const CHIME: VoiceDef = {
  partials: [
    { ratio: 1, gain: 1 },
    { ratio: 2, gain: 0.28, decayScale: 0.5 },
    { ratio: 3.01, gain: 0.08, decayScale: 0.3 },
  ],
  attack: 0.004,
  decay: 0.4,
  sustain: 0,
  release: 0.12,
  lowpassHz: 6000,
  lowpassQ: 0.4,
  gain: 0.3,
  octave: 0,
};

const SPARKLE: VoiceDef = {
  partials: [
    { ratio: 1, gain: 1 },
    { ratio: 2, gain: 0.12, decayScale: 0.5 },
  ],
  attack: 0.003,
  decay: 0.14,
  sustain: 0,
  release: 0.06,
  lowpassHz: 7000,
  lowpassQ: 0.3,
  gain: 0.16,
  octave: 0,
};

const BLIP: VoiceDef = {
  partials: [{ ratio: 1, gain: 1 }],
  attack: 0.003,
  decay: 0.035,
  sustain: 0,
  release: 0.03,
  lowpassHz: 5000,
  lowpassQ: 0.3,
  gain: 0.3,
  octave: 0,
};

const THUNK: VoiceDef = {
  partials: [
    { ratio: 1, gain: 1 },
    { ratio: 2, gain: 0.18, wave: 'triangle', decayScale: 0.5 },
  ],
  attack: 0.005,
  decay: 0.11,
  sustain: 0,
  release: 0.06,
  lowpassHz: 650,
  lowpassQ: 0.6,
  gain: 0.55,
  octave: 0,
};

const WOOD: VoiceDef = {
  partials: [
    { ratio: 1, gain: 1, wave: 'triangle' },
    { ratio: 2.52, gain: 0.2, decayScale: 0.5 },
  ],
  attack: 0.002,
  decay: 0.05,
  sustain: 0,
  release: 0.04,
  lowpassHz: 1900,
  lowpassQ: 0.7,
  gain: 0.32,
  octave: 0,
};

const SOFT_TONE: VoiceDef = {
  partials: [
    { ratio: 1, gain: 1, wave: 'triangle' },
    { ratio: 2, gain: 0.15 },
  ],
  attack: 0.025,
  decay: 0.5,
  sustain: 0.45,
  release: 0.35,
  lowpassHz: 1800,
  lowpassQ: 0.4,
  gain: 0.22,
  octave: 0,
};

// ───────────── Helpers ─────────────

const semi = (s: number): PitchRef => ({ semi: s });
const hz = (h: number): PitchRef => ({ hz: h });

/** Notes of an arpeggio: `semis` spaced `stepS` apart. */
function arpeggio(semis: readonly number[], stepS: number, dur: number, velocity: number, lastDur = dur): SfxNote[] {
  return semis.map((s, i) => ({
    at: i * stepS,
    pitch: semi(s),
    dur: i === semis.length - 1 ? lastDur : dur,
    velocity,
  }));
}

/** A chord (simultaneous notes) at `at`. */
function chord(semis: readonly number[], at: number, dur: number, velocity: number): SfxNote[] {
  return semis.map((s) => ({ at, pitch: semi(s), dur, velocity }));
}

// ───────────── Recipes ─────────────

export const SFX: Readonly<Record<SfxName, SfxSpec>> = {
  tap: {
    layers: [{ kind: 'tone', voice: BLIP, notes: [{ at: 0, pitch: hz(880), dur: 0.03, velocity: 0.45 }] }],
    ducksMusic: false,
    minIntervalMs: 45,
    priority: 'low',
  },
  spawn: {
    layers: [
      { kind: 'noise', at: 0, dur: 0.12, gain: 0.05, attack: 0.03, filter: { type: 'bandpass', hz: 1200, toHz: 2400, q: 1.2 } },
      { kind: 'tone', voice: BLIP, notes: [{ at: 0.02, pitch: semi(12), glideTo: semi(19), glideS: 0.08, dur: 0.08, velocity: 0.3 }] },
    ],
    ducksMusic: false,
    minIntervalMs: 200,
    priority: 'low',
  },
  // Bright rising chime: root → fifth → octave, with a soft sparkle on top.
  correct: {
    layers: [
      { kind: 'tone', voice: CHIME, notes: arpeggio([0, 7, 12], 0.055, 0.2, 0.8, 0.4) },
      { kind: 'tone', voice: SPARKLE, notes: [{ at: 0.11, pitch: semi(19), dur: 0.1, velocity: 0.5 }] },
    ],
    ducksMusic: true,
    minIntervalMs: 80,
    priority: 'high',
  },
  // Soft crumble (a few low-passed noise grains sweeping down) + a sparkle.
  layerBreak: {
    layers: [
      { kind: 'noise', at: 0, dur: 0.12, gain: 0.4, attack: 0.004, filter: { type: 'lowpass', hz: 1400, toHz: 500, q: 0.7 } },
      { kind: 'noise', at: 0.05, dur: 0.1, gain: 0.3, attack: 0.004, filter: { type: 'lowpass', hz: 1100, toHz: 400, q: 0.7 } },
      { kind: 'noise', at: 0.11, dur: 0.09, gain: 0.22, attack: 0.004, filter: { type: 'lowpass', hz: 900, toHz: 350, q: 0.7 } },
      { kind: 'noise', at: 0.17, dur: 0.08, gain: 0.14, attack: 0.004, filter: { type: 'lowpass', hz: 800, toHz: 300, q: 0.7 } },
      {
        kind: 'tone',
        voice: SPARKLE,
        notes: [
          { at: 0.06, pitch: semi(19), dur: 0.08, velocity: 0.55 },
          { at: 0.12, pitch: semi(24), dur: 0.08, velocity: 0.5 },
          { at: 0.18, pitch: semi(26), dur: 0.1, velocity: 0.45 },
        ],
      },
    ],
    ducksMusic: true,
    minIntervalMs: 80,
    priority: 'high',
  },
  // Sparkle cascade + a soft shimmer: the floor is clean!
  cleanFloor: {
    layers: [
      { kind: 'tone', voice: CHIME, notes: [{ at: 0, pitch: semi(12), dur: 0.4, velocity: 0.6 }] },
      { kind: 'tone', voice: SPARKLE, notes: arpeggio([12, 14, 19, 21, 24], 0.05, 0.08, 0.5, 0.2) },
      { kind: 'noise', at: 0.02, dur: 0.4, gain: 0.05, attack: 0.08, filter: { type: 'highpass', hz: 5000, q: 0.5 } },
    ],
    ducksMusic: true,
    minIntervalMs: 200,
    priority: 'high',
  },
  // Muted low "thunk" — a soft pitch drop, never a buzzer.
  wrong: {
    layers: [
      { kind: 'tone', voice: THUNK, notes: [{ at: 0, pitch: hz(170), glideTo: hz(105), glideS: 0.14, dur: 0.14, velocity: 0.8 }] },
      { kind: 'noise', at: 0, dur: 0.08, gain: 0.22, attack: 0.004, filter: { type: 'lowpass', hz: 320, q: 0.6 } },
    ],
    ducksMusic: true,
    minIntervalMs: 120,
    priority: 'high',
  },
  brickLand: {
    layers: [
      { kind: 'noise', at: 0, dur: 0.1, gain: 0.3, attack: 0.003, filter: { type: 'lowpass', hz: 450, toHz: 250, q: 0.6 } },
      { kind: 'tone', voice: THUNK, notes: [{ at: 0, pitch: hz(130), glideTo: hz(85), glideS: 0.08, dur: 0.08, velocity: 0.55 }] },
    ],
    ducksMusic: false,
    minIntervalMs: 90,
    priority: 'high',
  },
  // "Time's up": two soft wooden knocks.
  timeout: {
    layers: [
      {
        kind: 'tone',
        voice: WOOD,
        notes: [
          { at: 0, pitch: hz(620), dur: 0.05, velocity: 0.7 },
          { at: 0.14, pitch: hz(470), dur: 0.05, velocity: 0.6 },
        ],
      },
    ],
    ducksMusic: true,
    minIntervalMs: 200,
    priority: 'high',
  },
  // Rising arpeggio up to the octave + fifth.
  levelUp: {
    layers: [
      { kind: 'tone', voice: CHIME, notes: arpeggio([0, 2, 7, 9, 12, 19], 0.075, 0.15, 0.65, 0.6) },
      { kind: 'tone', voice: SPARKLE, notes: [{ at: 0.45, pitch: semi(24), dur: 0.15, velocity: 0.4 }] },
    ],
    ducksMusic: true,
    minIntervalMs: 800,
    priority: 'high',
  },
  // Gentle two-note fall (fifth → root): calm, not sad.
  levelDown: {
    layers: [{ kind: 'tone', voice: SOFT_TONE, notes: arpeggio([7, 0], 0.22, 0.2, 0.6, 0.45) }],
    ducksMusic: true,
    minIntervalMs: 800,
    priority: 'high',
  },
  // Soft open cadence resolving to the root (an octave lower, warm).
  gameOver: {
    layers: [
      {
        kind: 'tone',
        voice: SOFT_TONE,
        notes: [...chord([-3, 2], 0, 0.35, 0.45), ...chord([-5, 0], 0.4, 0.35, 0.45), ...chord([-12, -5, 0], 0.8, 0.9, 0.4)],
      },
    ],
    ducksMusic: true,
    minIntervalMs: 1500,
    priority: 'high',
  },
  // Very soft tick while the stack is high.
  danger: {
    layers: [{ kind: 'tone', voice: WOOD, notes: [{ at: 0, pitch: hz(1760), dur: 0.02, velocity: 0.22 }] }],
    ducksMusic: false,
    minIntervalMs: 350,
    priority: 'low',
  },
  // Tiny blip when a handwritten digit is recognised.
  hwRecognized: {
    layers: [{ kind: 'tone', voice: BLIP, notes: [{ at: 0, pitch: semi(19), glideTo: semi(24), glideS: 0.04, dur: 0.04, velocity: 0.35 }] }],
    ducksMusic: false,
    minIntervalMs: 80,
    priority: 'low',
  },
};

/** Frequency (Hz) of a pitch reference for a theme whose SFX root is `rootMidi`. */
export function resolvePitch(p: PitchRef, rootMidi: number): number {
  return 'hz' in p ? p.hz : midiToHz(rootMidi + p.semi);
}

/** Seconds from trigger until every voice of the effect has finished. */
export function sfxDurationS(spec: SfxSpec): number {
  let end = 0;
  for (const layer of spec.layers) {
    if (layer.kind === 'noise') end = Math.max(end, layer.at + layer.dur);
    else for (const n of layer.notes) end = Math.max(end, n.at + noteLifetimeS(layer.voice, n.dur));
  }
  return end;
}
